import type { InvitationPreview } from "@planerius/contracts/cabinet-auth"
import { useRouter } from "@tanstack/react-router"
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react"

import { selectTenant } from "../../api/auth.api"
import { acceptInvitation, acceptInvitationSignedIn, previewInvitation } from "../../api/invitations.api"
import { type ApiProblem, isApiProblem } from "../../api/problem"
import type { AccessState } from "../../access/access-state"
import { accessStore } from "../../access/access-store"
import { Button, Checkbox, TextField } from "../../ui"
import {
  type InviteForm,
  type InviteSession,
  TEXT_PASSWORD_HINT,
  acceptProblemAction,
  hasErrors,
  inviteFormErrors,
  inviteView,
} from "./invite-model"
import styles from "./invite-screen.module.css"

/**
 * 1-Э1.5 «Принятие приглашения» — публичный адрес /invite?token=…
 *
 * Состояния и тексты считает invite-model.ts; здесь только разметка и запросы. Сессия после
 * принятия не выдаётся: новый человек идёт на обычный вход, а тот, у кого учётка уже есть,
 * принимает приглашение из-под своего входа и сразу попадает в новый салон.
 */

const EMPTY_FORM: InviteForm = { displayName: "", email: "", password: "", terms: false, staffPersonalData: false }

export function InviteScreen(props: { readonly token: string | undefined }): ReactNode {
  const router = useRouter()
  const token = props.token ?? ""

  const [preview, setPreview] = useState<InvitationPreview | null>(null)
  const [invalid, setInvalid] = useState(token.length === 0)
  const [loadProblem, setLoadProblem] = useState<ApiProblem | null>(null)
  const [session, setSession] = useState<InviteSession | null>(null)

  const [form, setForm] = useState<InviteForm>(EMPTY_FORM)
  const [showErrors, setShowErrors] = useState(false)
  const [serverField, setServerField] = useState<{ readonly field: "password" | "email"; readonly text: string } | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<{ readonly email: string | null } | null>(null)
  // Вступление прошло, а открыть салон не удалось (заблокирован за неоплату, нужен второй
  // фактор): форма больше не нужна — повторное «Принять» ответило бы «уже принято».
  const [joined, setJoined] = useState<ApiProblem | null>(null)
  // Путь «это приглашение для меня» проверяет только согласия: имя и пароль там не нужны.
  const [consentsOnly, setConsentsOnly] = useState(false)

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (token.length === 0) return
      setLoadProblem(null)
      try {
        setPreview(await previewInvitation(token, signal))
        setInvalid(false)
      } catch (error) {
        if (signal?.aborted === true) return
        if (isApiProblem(error) && error.code === "invitation_invalid") {
          setInvalid(true)
          return
        }
        if (isApiProblem(error)) setLoadProblem(error)
        else throw error
      }
    },
    [token],
  )

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    // Кто уже вошёл в этом браузере: от этого зависит, чем кнопка докажет, что это вы.
    void accessStore.ensureLoaded().then((state) => {
      setSession(sessionOf(state))
      return state
    })
    return () => {
      controller.abort()
    }
  }, [load])

  const view = useMemo(() => inviteView({ preview, invalid, session }), [preview, invalid, session])

  const update = (patch: Partial<InviteForm>): void => {
    setForm((current) => ({ ...current, ...patch }))
    setMessage(null)
  }

  const fail = (error: unknown): void => {
    if (!isApiProblem(error)) throw error
    const action = acceptProblemAction(
      error,
      view?.kind === "form" ? { mode: view.mode, needsEmail: view.needsEmail } : undefined,
    )
    if (action.kind === "reload") {
      void load()
      return
    }
    if (action.kind === "field") {
      setServerField({ field: action.field, text: action.text })
      return
    }
    setMessage(action.text)
  }

  const acceptSignedIn = async (fromNewForm: boolean): Promise<void> => {
    setConsentsOnly(fromNewForm)
    setShowErrors(true)
    if (!form.terms || !form.staffPersonalData) return
    setBusy(true)
    let tenantId: string
    try {
      ;({ tenantId } = await acceptInvitationSignedIn({
        token,
        consents: { terms: true, staffPersonalData: true },
      }))
    } catch (error) {
      fail(error)
      setBusy(false)
      return
    }
    try {
      // Человек вступил в салон — туда и переходим: новый токен с этим салоном, свежий профиль.
      await selectTenant(tenantId)
      await accessStore.reload()
      router.history.push("/")
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setJoined(error)
    } finally {
      setBusy(false)
    }
  }

  const switchAccount = async (): Promise<void> => {
    await accessStore.signOut()
    await router.navigate({ to: "/login", search: { next: `/invite?token=${encodeURIComponent(token)}` } })
  }

  const submit = async (): Promise<void> => {
    if (view?.kind !== "form" || busy) return
    setServerField(null)
    setMessage(null)

    if (view.mode === "sign_in") {
      // Вход вернёт сюда же: адрес приглашения пропускается и без выбранного салона.
      await router.navigate({ to: "/login", search: { next: `/invite?token=${encodeURIComponent(token)}` } })
      return
    }
    if (view.mode === "signed_in") {
      await acceptSignedIn(false)
      return
    }

    setConsentsOnly(false)
    setShowErrors(true)
    if (hasErrors(inviteFormErrors(form, view.mode, view.needsEmail))) return
    setBusy(true)
    try {
      const email = view.needsEmail ? form.email.trim().toLowerCase() : undefined
      await acceptInvitation({
        token,
        consents: { terms: true, staffPersonalData: true },
        displayName: form.displayName.trim(),
        password: form.password,
        ...(email === undefined ? {} : { email }),
      })
      setDone({ email: email ?? null })
    } catch (error) {
      fail(error)
    } finally {
      setBusy(false)
    }
  }

  if (done !== null && preview !== null) {
    return (
      <Shell
        footer={
          <Button variant="primary" onClick={() => void router.navigate({ to: "/login", search: { next: undefined } })}>
            Войти
          </Button>
        }
      >
        <h1 className={styles.heading}>Готово — вы в «{preview.tenantName}»</h1>
        <p className={styles.text}>
          {done.email === null
            ? "Войдите с почтой, на которую пришло приглашение, и паролем, который только что задали."
            : `Войдите с почтой ${done.email} и паролем, который только что задали.`}
        </p>
      </Shell>
    )
  }

  if (joined !== null && preview !== null) {
    return (
      <Shell
        footer={
          <Button variant="primary" onClick={() => void router.navigate({ to: "/choose-salon", search: { next: undefined } })}>
            К выбору салона
          </Button>
        }
      >
        <h1 className={styles.heading}>Вы вступили в «{preview.tenantName}»</h1>
        <p className={styles.text}>
          Приглашение принято, но открыть салон сейчас не получилось: {joined.detail ?? joined.title}. Салон уже
          в вашем списке — войдите в него с экрана выбора салона, когда это станет возможно.
        </p>
      </Shell>
    )
  }

  if (loadProblem !== null) {
    return (
      <Shell
        footer={
          <Button variant="primary" onClick={() => void load()}>
            Повторить
          </Button>
        }
      >
        <h1 className={styles.heading}>Не удалось открыть приглашение</h1>
        <p className={styles.text}>{loadProblem.detail ?? loadProblem.title}</p>
      </Shell>
    )
  }

  if (view === null) {
    return (
      <Shell footer={null}>
        <p className={styles.text} role="status">
          Открываем приглашение…
        </p>
      </Shell>
    )
  }

  if (view.kind !== "form") {
    return (
      <Shell
        footer={
          view.kind === "accepted" ? (
            <Button variant="primary" onClick={() => void router.navigate({ to: "/login", search: { next: undefined } })}>
              {view.button}
            </Button>
          ) : null
        }
      >
        <h1 className={styles.heading}>{view.heading}</h1>
        <p className={styles.text}>{view.text}</p>
      </Shell>
    )
  }

  const errors = !showErrors
    ? null
    : consentsOnly
      ? { displayName: null, email: null, password: null, consents: inviteFormErrors(form, "signed_in", false).consents }
      : inviteFormErrors(form, view.mode, view.needsEmail)
  const passwordError = serverField?.field === "password" ? serverField.text : (errors?.password ?? undefined)
  const emailError = serverField?.field === "email" ? serverField.text : (errors?.email ?? undefined)

  return (
    <Shell
      footer={
        <Button variant="primary" loading={busy} loadingLabel="Принимаем…" onClick={() => void submit()}>
          {view.button}
        </Button>
      }
    >
      <h1 className={styles.heading}>{view.heading}</h1>

      <div className={styles.roleCard}>
        <span className={styles.roleLabel}>Ваша роль</span>
        <span className={styles.roleName}>{view.role.name}</span>
        {view.role.summary === null ? null : <span className={styles.roleSummary}>{view.role.summary}</span>}
        <span className={styles.roleBranches}>{view.role.branches}</span>
      </div>

      {view.role.mfaWarning === null ? null : <div className={styles.warning}>{view.role.mfaWarning}</div>}
      {view.notice === null ? null : <div className={styles.notice}>{view.notice}</div>}

      {view.mode === "new" ? (
        <>
          <TextField
            className={styles.field}
            label="Как вас называть"
            name="name"
            autoComplete="name"
            value={form.displayName}
            error={errors?.displayName ?? undefined}
            onChange={(event) => {
              update({ displayName: event.target.value })
            }}
          />
          {view.needsEmail ? (
            <TextField
              className={styles.field}
              label="Почта для входа"
              hint="Приглашение пришло на телефон, а вход в кабинет — по почте."
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              spellCheck={false}
              value={form.email}
              error={emailError}
              onChange={(event) => {
                update({ email: event.target.value })
                setServerField(null)
              }}
            />
          ) : null}
          <TextField
            className={styles.field}
            label="Придумайте пароль"
            hint={TEXT_PASSWORD_HINT}
            name="new-password"
            type="password"
            autoComplete="new-password"
            value={form.password}
            error={passwordError}
            onChange={(event) => {
              update({ password: event.target.value })
              setServerField(null)
            }}
          />
        </>
      ) : null}

      {view.mode === "sign_in" ? null : (
        <div className={styles.consents}>
          {/* Ссылок на документы пока нет: текстов условий и политики для сотрудников ещё
              не существует. Ссылка в никуда хуже её отсутствия (вопрос владельцу). */}
          <Checkbox
            checked={form.terms}
            onChange={(checked) => {
              update({ terms: checked })
            }}
          >
            Принимаю условия использования
          </Checkbox>
          <div className={styles.consentDivider} />
          <Checkbox
            checked={form.staffPersonalData}
            onChange={(checked) => {
              update({ staffPersonalData: checked })
            }}
          >
            Согласен на обработку моих данных как сотрудника — отдельный документ
          </Checkbox>
          {errors?.consents === null || errors === null ? null : (
            <small className={styles.error}>{errors.consents}</small>
          )}
        </div>
      )}

      {view.alsoAsSignedIn === null ? null : (
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => {
            void acceptSignedIn(true)
          }}
        >
          {view.alsoAsSignedIn}
        </button>
      )}

      {view.switchAccount === null ? null : (
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => {
            void switchAccount()
          }}
        >
          {view.switchAccount}
        </button>
      )}

      {message === null ? null : (
        <div className={styles.error} role="alert">
          {message}
        </div>
      )}
    </Shell>
  )
}

/** Кто вошёл: имя есть только у готового профиля; без выбранного салона профиля нет. */
function sessionOf(state: AccessState): InviteSession | null {
  if (state.kind === "ready") return { displayName: state.context.user.displayName }
  if (state.kind === "tenant_required") return { displayName: null }
  return null
}

function Shell(props: { readonly children: ReactNode; readonly footer: ReactNode }): ReactNode {
  return (
    <div className={styles.page}>
      <main className={styles.column}>{props.children}</main>
      {props.footer === null ? null : <div className={styles.footer}>{props.footer}</div>}
    </div>
  )
}
